// First-party signal hosts, one set per brand zone:
//   `telemetry.*` — Faro RUM        `analytics.*` — OpenPanel
//   `errors.*`    — Sentry tunnel   `signals.*`   — Mixpanel proxy
//   `search.*`    — Meilisearch (consumed via `@navio/observability/hosts` by
//     `noddi-ui-web` spotlight, which must not pull in the SDK-bearing entry)
// Brand zones: `noddi.co`, `noddi.no`, `bynavio.com`, `bynav.io` (mirrors the
// noddi-infrastructure `FIRST_PARTY_ZONES` registry; ingress + CORS live in
// `services/traefik`, `services/alloy`, `services/open_panel`).
// The customer SPA ships as ONE bundle served on noddi.{co,no} plus the
// bynavio.com / bynav.io brand hosts (and white-label partner hosts on .no),
// so the host is picked at runtime from the page's brand zone — a fixed
// build-time URL would be cross-site (ad-blocker bait + CORS preflight
// failures) on every other zone.
//
// Only rewrites hostnames that are exactly `<signal>.<brand-zone>`. Other URLs
// (custom Alloy, localhost proxy, third-party) pass through. Page hosts on no
// known brand zone (Netlify previews, localhost) keep the configured URL as
// the fallback.

const SIGNAL_HOST = /^(telemetry|analytics|errors|signals|search)\.(noddi\.co|noddi\.no|bynavio\.com|bynav\.io)$/i;

function pageHostname(hostname: string | undefined): string {
  if (hostname !== undefined) {
    return hostname;
  }
  if (typeof globalThis !== 'undefined' && 'location' in globalThis) {
    return (globalThis as { location?: { hostname?: string } }).location?.hostname ?? '';
  }
  return '';
}

function parseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

// Brand zone the page is served on. The broad .no/.co tail keeps the legacy
// behavior: white-label partner hosts (e.g. `booking.bestdrive.no`) use the
// matching noddi zone even though they are not `*.noddi.*` hosts.
function pageBrandZone(hostname: string): string | undefined {
  const host = hostname.toLowerCase();
  if (host === 'bynavio.com' || host.endsWith('.bynavio.com')) {
    return 'bynavio.com';
  }
  if (host === 'bynav.io' || host.endsWith('.bynav.io')) {
    return 'bynav.io';
  }
  if (host.endsWith('.no')) {
    return 'noddi.no';
  }
  if (host.endsWith('.co')) {
    return 'noddi.co';
  }
  return undefined;
}

export function resolveNoddiDualTldUrl(configuredUrl: string | undefined, hostname?: string): string | undefined {
  if (!configuredUrl) {
    return configuredUrl;
  }

  const url = parseUrl(configuredUrl);
  if (!url) {
    return configuredUrl;
  }

  const match = url.hostname.match(SIGNAL_HOST);
  if (!match) {
    return configuredUrl;
  }

  const prefix = match[1]?.toLowerCase();
  const zone = pageBrandZone(pageHostname(hostname));
  if (!prefix || !zone) {
    return configuredUrl;
  }

  url.hostname = `${prefix}.${zone}`;
  return url.toString();
}
